/**
 * OrderListHeader
 *
 * Header component for the expandable order list.
 * Features Select All checkbox, title with count, search, and expand/collapse controls.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Layers } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

interface OrderListHeaderProps {
  /** Total number of orders in the list */
  totalOrders: number;
  /** Callback to expand all orders */
  onExpandAll: () => void;
  /** Callback to collapse all orders */
  onCollapseAll: () => void;
  /** Whether all orders are expanded */
  allExpanded: boolean;
  /** Whether no orders are expanded */
  noneExpanded: boolean;
  /** Current search value */
  searchValue: string;
  /** Callback when search value changes */
  onSearchChange: (value: string) => void;
  /** Optional bulk edit handler */
  onBulkEdit?: () => void;
  /** Whether bulk edit is available */
  showBulkEdit?: boolean;
  /** Whether all orders are selected */
  isAllSelected?: boolean;
  /** Whether some orders are selected (for indeterminate state) */
  isSomeSelected?: boolean;
  /** Number of currently selected orders */
  selectedCount?: number;
  /** Callback to toggle select all */
  onSelectAll?: () => void;
  /** Whether to show selection controls */
  showSelection?: boolean;
  /** Optional Display menu, rendered first in the right cluster. */
  displayMenu?: ReactNode;
}

export function OrderListHeader({
  totalOrders,
  onExpandAll,
  onCollapseAll,
  allExpanded,
  noneExpanded,
  searchValue,
  onSearchChange,
  onBulkEdit,
  showBulkEdit = false,
  isAllSelected = false,
  isSomeSelected = false,
  selectedCount = 0,
  onSelectAll,
  showSelection = false,
  displayMenu,
}: OrderListHeaderProps) {
  const { t } = useTranslation('production');
  // Determine checkbox state
  const isIndeterminate = isSomeSelected && !isAllSelected;

  return (
    <div className="flex items-center justify-between gap-4 p-4 border-b bg-card">
      {/* Left: Select All + Title with count */}
      <div className="flex items-center gap-3">
        {showSelection && onSelectAll && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={isAllSelected}
              ref={(el) => {
                if (el) {
                  // Set indeterminate state via DOM (not supported as prop)
                  (el as HTMLButtonElement & { indeterminate?: boolean }).indeterminate = isIndeterminate;
                }
              }}
              onCheckedChange={() => onSelectAll()}
              className="h-5 w-5"
              aria-label={isAllSelected ? t('orders.deselectAllOrders') : t('orders.selectAllOrders')}
            />
            {selectedCount > 0 && (
              <span className="text-sm text-muted-foreground">
                {t('orders.selected', { count: selectedCount })}
              </span>
            )}
          </div>
        )}
        <h2 className="text-lg font-semibold text-foreground">{t('orders.title')}</h2>
        <Badge variant="default" size="sm">
          {totalOrders}
        </Badge>
      </div>

      {/* Center: Search */}
      <div className="flex-1 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('orders.searchPlaceholder')}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {displayMenu}

        {showBulkEdit && onBulkEdit && (
          <Button variant="outline" size="sm" onClick={onBulkEdit} className="gap-2">
            <Layers className="h-4 w-4" />
            {t('orders.bulkEdit')}
          </Button>
        )}

        {/* Expand / Collapse All */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onExpandAll}
          disabled={allExpanded}
          className="text-muted-foreground hover:text-foreground"
        >
          {t('orders.expandAll')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCollapseAll}
          disabled={noneExpanded}
          className="text-muted-foreground hover:text-foreground"
        >
          {t('orders.collapseAll')}
        </Button>
      </div>
    </div>
  );
}
